import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { Flair } from "../../../components/Flair";
import { Image } from "../../../components/Image";
import { Link } from "../../../components/Link";
import { RatingGraphic } from "../../../components/RatingGraphic";
import { RichText } from "../../../components/RichText";
import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { IProductID, WebPageURL } from "../../../models/nominals";
import { ICompareTile, IProductFlair } from "../../../models/product-compare";
import { getShopNowURL } from "../selectors";

import styles from "./index.module.scss";

const getVariantTitle = (
    selectedVariant: IProduct | null,
    data: ICompareTile,
): string => {
    return selectedVariant
        ? selectedVariant.title
        : (data.rootProducts.at(0)?.title ?? "");
};

const getVariantURL = (
    selectedVariant: IProduct | null,
    data: ICompareTile,
): WebPageURL | null => {
    return selectedVariant?.link
        ? selectedVariant?.link
        : (data.rootProducts.at(0)?.link ?? null);
};

const calculateNumReviews = (data: ICompareTile): number => {
    return data.rootProducts.reduce((memo, rp) => memo + rp.num_reviews, 0);
};

const calculateAvgRating = (data: ICompareTile): number => {
    const total = data.rootProducts.reduce(
        (memo, rp) => memo + (rp.rating || 0),
        0,
    );
    const products = data.rootProducts.filter((rp) => rp.rating !== null);

    return total / products.length;
};

const buildRating = (
    showRating: boolean | undefined,
    selectedVariant: IProduct | null,
    numReviews: number,
    avgRating: number,
    theme: ProductCompareTheme | null | undefined,
    variantURL: WebPageURL | null,
): React.ReactNode => {
    if (!showRating) {
        return null;
    }
    if (!selectedVariant) {
        return null;
    }
    if (numReviews < 1) {
        return null;
    }
    if (theme === ProductCompareTheme.SELECT) {
        return (
            <div className={styles.reviewLink}>
                <RatingGraphic
                    cardClass={styles.starRating}
                    cardSize="large"
                    numReviews={numReviews}
                    rating={avgRating}
                />
            </div>
        );
    }
    return (
        <a
            className={styles.reviewLink}
            href={`${variantURL}#reviews__body`}
            title={t`Read Reviews`}
        >
            <RatingGraphic
                cardClass={styles.starRating}
                cardSize="large"
                numReviews={numReviews}
                rating={avgRating}
            />
        </a>
    );
};

const renderHeader = (
    data: ICompareTile,
    selectedVariant: IProduct | null,
    pageRootProduct: IProductID | null | undefined,
    theme: ProductCompareTheme | null | undefined,
    variantTitle: string,
    numReviews: number,
    avgRating: number,
    showRating: boolean | undefined,
    variantURL: WebPageURL | null,
    shopNowURL: WebPageURL | null,
): React.ReactNode => {
    const showSelectedFlair =
        theme === ProductCompareTheme.SELECT
            ? data.product_compare_tile.product_id === pageRootProduct
            : false;
    const selectedFlairData = {
        content: "SELECTED",
        style: "flair--selected",
    };
    const selectedFlair = <Flair flair={selectedFlairData} />;
    if (theme === ProductCompareTheme.SELECT) {
        return (
            <header className={styles.root}>
                {showSelectedFlair && selectedFlair}
                <Image
                    src={data.product_compare_tile.image.url}
                    alt={data.product_compare_tile.image.title}
                />
                {data.secondary_flair && <Flair flair={data.secondary_flair} />}
                <RichText
                    html={data.product_compare_tile.title}
                    className={`title ${styles.richText}`}
                />
            </header>
        );
    }
    const rootClasses = classNames({
        [styles.root]: true,
        [theme || ""]: true,
    });
    const showSecondFlairBelowRating =
        theme &&
        [ProductCompareTheme.STEP_UP, ProductCompareTheme.THEME_ONE].includes(
            theme,
        );
    const imageTitle = data.product_compare_tile.image.title?.trim();

    return (
        <header className={rootClasses}>
            <Link
                href={shopNowURL}
                title={interpolate(t`Learn more about %s`, [variantTitle])}
            >
                {showSelectedFlair && selectedFlair}
                <Image
                    src={data.product_compare_tile.image.url}
                    alt={
                        imageTitle
                            ? imageTitle
                            : interpolate(t`Learn more about %s`, [
                                  variantTitle,
                              ])
                    }
                />
                {data.secondary_flair && !showSecondFlairBelowRating && (
                    <div className="secondary-flair">
                        <Flair flair={data.secondary_flair} />
                    </div>
                )}
                <RichText
                    html={data.product_compare_tile.title}
                    className={classNames("title", styles.richText, theme)}
                />
            </Link>
            {buildRating(
                showRating,
                selectedVariant,
                numReviews,
                avgRating,
                theme,
                variantURL,
            )}
            {data.secondary_flair && showSecondFlairBelowRating && (
                <div className="secondary-flair">
                    <Flair flair={data.secondary_flair} />
                </div>
            )}
        </header>
    );
};

const renderPrimaryFlair = (
    flair: IProductFlair,
    theme: ProductCompareTheme | null | undefined,
    variantTitle: string,
    shopNowURL: WebPageURL | null,
): React.ReactNode => {
    if (theme === ProductCompareTheme.STEP_UP) {
        return (
            <Link
                href={shopNowURL}
                title={interpolate(t`Learn more about %s`, [variantTitle])}
            >
                <div className="primary-flair">
                    <Flair flair={flair} />
                </div>
            </Link>
        );
    }

    return <Flair flair={flair} />;
};

export const ProductCompareTileHeader = (
    props: Immutable<{
        data: ICompareTile;
        selectedVariant: IProduct | null;
        pageRootProduct?: IProductID | null;
        theme?: ProductCompareTheme | null;
        showDescription?: boolean;
        showRating?: boolean;
    }>,
): React.ReactNode => {
    const variantTitle = getVariantTitle(props.selectedVariant, props.data);
    // URLs are now locale-aware from the backend
    const variantURL = getVariantURL(props.selectedVariant, props.data);
    const shopNowURL = getShopNowURL(props.data, props.selectedVariant);

    const numReviews = calculateNumReviews(props.data);
    const avgRating = calculateAvgRating(props.data);

    const descriptionClasses = classNames({
        [styles.description]: true,
        [styles.descriptionThemeStepUp]:
            props.theme === ProductCompareTheme.STEP_UP,
        [styles.descriptionThemeSelect]:
            props.theme === ProductCompareTheme.SELECT,
        [styles.descriptionThemeOne]:
            props.theme === ProductCompareTheme.THEME_ONE,
    });

    return (
        <>
            {props.data.flair &&
                renderPrimaryFlair(
                    props.data.flair,
                    props.theme,
                    variantTitle,
                    shopNowURL,
                )}
            {renderHeader(
                props.data,
                props.selectedVariant,
                props.pageRootProduct,
                props.theme,
                variantTitle,
                numReviews,
                avgRating,
                props.showRating,
                variantURL,
                shopNowURL,
            )}
            {props.showDescription && (
                <RichText
                    className={descriptionClasses}
                    html={props.data.product_compare_tile.short_description}
                    inline={true}
                />
            )}
        </>
    );
};
